import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import {
  Calendar,
  Users,
  CheckCircle2,
  TrendingUp,
  Plus,
  ArrowRight,
  Clock,
  FileText,
  BarChart3,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Dashboard = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/events')
      .then((res) => setEvents(res.data || []))
      .finally(() => setLoading(false));
  }, []);

  const upcomingEvents = events.filter((e) => e.status === 'upcoming').slice(0, 3);
  const totalGuests = events.reduce((sum, e) => sum + (e.totalGuests || 0), 0);
  const totalAttendance = events.reduce((sum, e) => sum + (e.attendedGuests || 0), 0);
  const totalRegistered = events.reduce((sum, e) => sum + (e.registeredGuests || 0), 0);
  const attendanceRate = totalRegistered > 0 ? ((totalAttendance / totalRegistered) * 100).toFixed(1) : 0;

  const monthlyEvents = Object.values(
    events.reduce((acc, e) => {
      const month = e.date ? new Date(e.date).toLocaleString('en-US', { month: 'short' }) : 'N/A';
      acc[month] = acc[month] || { month, events: 0 };
      acc[month].events += 1;
      return acc;
    }, {})
  );

  const stats = [
    {
      label: 'Total Events',
      value: events.length,
      icon: Calendar,
      color: 'bg-blue-100 dark:bg-blue-900/20',
      iconColor: 'text-blue-600',
    },
    {
      label: 'Total Guests',
      value: totalGuests,
      icon: Users,
      color: 'bg-green-100 dark:bg-green-900/20',
      iconColor: 'text-green-600',
    },
    {
      label: 'Total Attendance',
      value: totalAttendance,
      icon: CheckCircle2,
      color: 'bg-purple-100 dark:bg-purple-900/20',
      iconColor: 'text-purple-600',
    },
    {
      label: 'Attendance Rate',
      value: `${attendanceRate}%`,
      icon: TrendingUp,
      color: 'bg-orange-100 dark:bg-orange-900/20',
      iconColor: 'text-orange-600',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Welcome back! Here's your event overview.</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => navigate('/dashboard/events/create')}>
          <Plus className="w-4 h-4 mr-2" />
          Create Event
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="p-6 hover:shadow-md transition-smooth">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Monthly Events</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={monthlyEvents}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }} cursor={{ fill: '#f3f4f6' }} />
              <Bar dataKey="events" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Upcoming Events</h2>
            <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700" onClick={() => navigate('/dashboard/events')}>
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="space-y-3">
            {upcomingEvents.map((event) => (
              <div
                key={event.id}
                className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-smooth cursor-pointer"
                onClick={() => navigate(`/dashboard/events/${event.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{event.name}</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(event.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{event.time}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-sm">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-400">
                        {event.registeredGuests}/{event.totalGuests} registered
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Button variant="outline" className="justify-start h-auto py-4" onClick={() => navigate('/dashboard/events/create')}>
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20 mr-3">
              <Plus className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-gray-900 dark:text-white">Create Event</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Start new event</div>
            </div>
          </Button>
          <Button variant="outline" className="justify-start h-auto py-4" onClick={() => navigate('/dashboard/guests')}>
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/20 mr-3">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-gray-900 dark:text-white">Manage Guests</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Add or edit guests</div>
            </div>
          </Button>
          <Button variant="outline" className="justify-start h-auto py-4" onClick={() => navigate('/dashboard/templates')}>
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/20 mr-3">
              <FileText className="w-5 h-5 text-purple-600" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-gray-900 dark:text-white">Browse Templates</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">View invitation templates</div>
            </div>
          </Button>
          <Button variant="outline" className="justify-start h-auto py-4" onClick={() => navigate('/dashboard/reports')}>
            <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/20 mr-3">
              <BarChart3 className="w-5 h-5 text-orange-600" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-gray-900 dark:text-white">View Reports</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Check analytics</div>
            </div>
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default Dashboard;
